import React, { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { apiClient } from "@/services/api";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Link as MuiLink,
  Alert,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  OutlinedInput,
  InputAdornment,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Container,
  CircularProgress,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
// @ts-ignore
import WechatLoginDialog from "@/components/auth/WechatLoginDialog";
import AgreementCheckbox from "@/components/auth/AgreementCheckbox";

interface LoginFormData {
  username: string;
  password: string;
}

interface SmsFormData {
  phone: string;
  code: string;
}

const loginSchema = yup.object({
  username: yup.string().required("用户名不能为空"),
  password: yup.string().min(6, "密码至少6位").required("密码不能为空"),
});

const smsSchema = yup.object({
  phone: yup
    .string()
    .matches(/^1[3-9]\d{9}$/, "请输入正确的手机号")
    .required("手机号不能为空"),
  code: yup.string().length(6, "验证码必须是6位").required("验证码不能为空"),
});

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const {
    login,
    loginWithSms,
    isAuthenticated,
    loading: authLoading,
  } = useAuth();
  const [activeTab, setActiveTab] = useState(0);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [smsLoading, setSmsLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState("");
  const [wechatDialogOpen, setWechatDialogOpen] = useState(false);
  const [registerDialogOpen, setRegisterDialogOpen] = useState(false);
  const [agreementChecked, setAgreementChecked] = useState(false);

  // Check if user is already authenticated and redirect to home
  useEffect(() => {
    if (!authLoading && isAuthenticated) {
      // 使用 replace 模式避免浏览器历史记录中的登录页面
      navigate("/", { replace: true });
    }
  }, [isAuthenticated, authLoading, navigate]);

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          position: "relative",
          display: "flex",
          flexDirection: "column",
          backgroundImage: "url(/imgs/zybg3.jpg)",
          backgroundSize: "cover",
          backgroundPosition: "center",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <CircularProgress sx={{ color: "white" }} />
      </Box>
    );
  }

  const {
    register: registerLogin,
    handleSubmit: handleLoginSubmit,
    formState: { errors: loginErrors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
  });

  const {
    register: registerSms,
    handleSubmit: handleSmsSubmit,
    watch,
    formState: { errors: smsErrors },
  } = useForm<SmsFormData>({
    resolver: yupResolver(smsSchema),
  });

  const phoneValue = watch("phone");

  const onLoginSubmit = async (data: LoginFormData) => {
    try {
      if (!agreementChecked) {
        toast.error("请先同意用户协议和隐私条款");
        return;
      }
      
      setLoading(true);
      setError("");
      await login(data);
      toast.success("登录成功");
      navigate("/");
    } catch (err: any) {
      setError(err.message || "登录失败");
    } finally {
      setLoading(false);
    }
  };

  const onSmsSubmit = async (data: SmsFormData) => {
    try {
      if (!agreementChecked) {
        toast.error("请先同意用户协议和隐私条款");
        return;
      }
      
      setLoading(true);
      setError("");
      await loginWithSms(data.phone, data.code);
      toast.success("登录成功");
      navigate("/");
    } catch (err: any) {
      setError(err.message || "登录失败");
    } finally {
      setLoading(false);
    }
  };

  const sendSmsCode = async () => {
    if (!phoneValue || !/^1[3-9]\d{9}$/.test(phoneValue)) {
      toast.error("请输入正确的手机号");
      return;
    }

    try {
      setSmsLoading(true);
      await apiClient.auth.sendSmsCode(phoneValue);
      toast.success("验证码已发送");
      setCountdown(60);
      const timer = setInterval(() => {
        setCountdown((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    } catch (err: any) {
      toast.error(err.message || "发送失败");
    } finally {
      setSmsLoading(false);
    }
  };

  const handleWechatLoginSuccess = (_userInfo: any) => {
    toast.success("微信登录成功");
    navigate("/");
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        backgroundImage: "url(/imgs/zybg3.jpg)",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
        position: "relative",
        "&::before": {
          content: '""',
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0, 0, 0, 0.3)", // 添加半透明遮罩层以确保文字可读性
          zIndex: 1,
        },
      }}
    >
      {/* 顶部居中大标题 */}
      <Box
        sx={{
          position: "absolute",
          top: { xs: 24, md: 32 },
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 2,
          textAlign: "center",
          color: "#fff",
        }}
      >
        <Typography
          sx={{
            fontWeight: "bold",
            fontSize: { xs: 36, md: 56, lg: 64 },
            letterSpacing: 2,
            textShadow: "0 4px 12px rgba(0,0,0,0.6)",
          }}
        >
          昭元绩效AI平台
        </Typography>
      </Box>

      <Container
        maxWidth="xl"
        sx={{
          position: "absolute",
          top: "50%",
          left: 0,
          right: 0,
          transform: "translateY(-50%)",
          px: { xs: 2, md: 4 },
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          zIndex: 2,
        }}
      >
        {/* 左侧品牌与标语 */}
        <Box
          sx={{
            flex: 1,
            pr: { xs: 2, md: 6 },
            ml: { xs: 0, md: 2 },
            maxWidth: { md: "60%" },
            color: "#fff",
            textAlign: "left",
            alignSelf: "center",
          }}
        >
          <Typography
            sx={{
              fontWeight: "bold",
              mb: 2,
              textShadow: "0 2px 6px rgba(0,0,0,0.5)",
              fontSize: { xs: 22, md: 30, lg: 36 },
            }}
          >
            从三人半月，到一人一天，AI快速生成报告
          </Typography>
          <Typography
            sx={{
              fontWeight: "bold",
              textShadow: "0 2px 8px rgba(0,0,0,0.6)",
              fontSize: { xs: 30, md: 44, lg: 56 },
              lineHeight: 1.2,
            }}
          >
            专家级报告，新手级操作
          </Typography>

          {/* 客服信息：放在标语下方 */}
          <Box
            sx={{
              mt: 8,
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
            }}
          >
            {/* 微信二维码和免费试用提示 */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 3, mb: 2 }}>
              {/* 微信二维码 */}
              <Box sx={{ textAlign: "center" }}>
                <img
                  src="/imgs/kefu.png"
                  alt="客服微信二维码"
                  style={{
                    width: 160,
                    height: 160,
                    borderRadius: 8,
                    border: "2px solid rgba(255,255,255,0.3)",
                  }}
                />
              </Box>

              {/* 免费试用提示 - 在二维码旁边 */}
              <Typography
                sx={{
                  fontSize: { xs: 14, md: 16 },
                  color: "#FFA500",
                  fontWeight: "bold",
                  textShadow: "0 2px 4px rgba(0,0,0,0.5)",
                  whiteSpace: "nowrap",
                  maxWidth: "200px",
                  lineHeight: 1.3,
                }}
              >
                新注册用户免费试用一个重点绩效评价项目
              </Typography>
            </Box>

            {/* 客服联系方式 */}
            <Box sx={{ width: 160 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.9)", textAlign: "left" }}
                >
                  客服电话：
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.9)", textAlign: "right" }}
                >
                  15515993457
                </Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography
                  variant="body2"
                  sx={{
                    color: "rgba(255,255,255,0.9)",
                    textAlign: "left",
                    whiteSpace: "pre",
                  }}
                >
                  微       信：
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "rgba(255,255,255,0.9)", textAlign: "right" }}
                >
                  15515993457
                </Typography>
              </Box>
            </Box>

            {/* 产品文档包下载链接 */}
            <Box
              sx={{
                mt: 6, // 进一步增加上边距
                pt: 3, // 增加内边距
                position: "relative",
                "&::before": {
                  content: '""',
                  position: "absolute",
                  top: 0,
                  left: 0,
                  width: "600%", // 增加长度到6倍
                  height: "1px",
                  backgroundColor: "#A5A5A5",
                },
              }}
            >
              <MuiLink
                href="/api/download/all-docs"
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: "rgba(255, 255, 255, 0.9)",
                  textDecoration: "none",
                  display: "flex",
                  alignItems: "center",
                  gap: 0.5,
                  fontSize: "14px",
                  "&:hover": {
                    textDecoration: "underline",
                    color: "#ffffff",
                  },
                }}
              >
                📦 产品文档包
              </MuiLink>
            </Box>
          </Box>
        </Box>

        {/* 右侧登录卡片 - 居中垂直，靠右 */}
        <Box sx={{ width: 420, ml: "auto", mr: { xs: 2, md: 8 } }}>
          <Paper
            elevation={6}
            sx={{
              p: 4,
              borderRadius: 3,
              width: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.98)", // 增加不透明度，确保在暗色模式下有足够对比度
              backdropFilter: "blur(10px)", // 毛玻璃效果
              border: "1px solid rgba(255, 255, 255, 0.3)",
              // 确保在暗色模式下文字可见
              "& .MuiTypography-root": {
                color: "rgba(0, 0, 0, 0.87)",
              },
            }}
          >
            <Box sx={{ textAlign: "center", mb: 2 }}>
              <Typography variant="h5" component="h2" gutterBottom>
                登录
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }}>
                {error}
              </Alert>
            )}

            {/* 登录方式页签 */}
            <Tabs
              value={activeTab}
              onChange={(_, v) => setActiveTab(v)}
              variant="fullWidth"
              sx={{ mb: 1 }}
            >
              <Tab label="密码登录" />
              <Tab label="验证码登录" />
            </Tabs>

            {activeTab === 0 && (
              <form onSubmit={handleLoginSubmit(onLoginSubmit)}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="username"
                  label="手机号"
                  autoComplete="username"
                  autoFocus
                  {...registerLogin("username")}
                  disabled={loading}
                  placeholder="请输入手机号"
                  error={!!loginErrors.username}
                  helperText={loginErrors.username?.message}
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(0, 0, 0, 0.7)",
                      "&.Mui-focused": {
                        color: "primary.main",
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: "rgba(0, 0, 0, 0.87)",
                      "&::placeholder": {
                        color: "rgba(0, 0, 0, 0.5)",
                        opacity: 1,
                      },
                    },
                  }}
                />

                <FormControl fullWidth margin="normal" variant="outlined">
                  <InputLabel htmlFor="password">密码</InputLabel>
                  <OutlinedInput
                    id="password"
                    type={showPassword ? "text" : "password"}
                    {...registerLogin("password")}
                    error={!!loginErrors.password}
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </InputAdornment>
                    }
                    label="密码"
                    sx={{
                      "& .MuiInputBase-root": {
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                      },
                    }}
                  />
                </FormControl>
                {loginErrors.password && (
                  <Typography color="error" variant="caption">
                    {loginErrors.password.message}
                  </Typography>
                )}

                <AgreementCheckbox
                  checked={agreementChecked}
                  onChange={setAgreementChecked}
                  disabled={loading}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2, mb: 2, py: 1.5 }}
                  disabled={loading || !agreementChecked}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "登录"
                  )}
                </Button>
              </form>
            )}

            {activeTab === 1 && (
              <form onSubmit={handleSmsSubmit(onSmsSubmit)}>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  label="手机号"
                  {...registerSms("phone")}
                  error={!!smsErrors.phone}
                  helperText={smsErrors.phone?.message}
                  placeholder="请输入手机号"
                  sx={{
                    "& .MuiInputBase-root": {
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      "&:hover": {
                        backgroundColor: "rgba(255, 255, 255, 0.95)",
                      },
                      "&.Mui-focused": {
                        backgroundColor: "rgba(255, 255, 255, 1)",
                      },
                    },
                    "& .MuiInputLabel-root": {
                      color: "rgba(0, 0, 0, 0.7)",
                      "&.Mui-focused": {
                        color: "primary.main",
                      },
                    },
                    "& .MuiInputBase-input": {
                      color: "rgba(0, 0, 0, 0.87)",
                      "&::placeholder": {
                        color: "rgba(0, 0, 0, 0.5)",
                        opacity: 1,
                      },
                    },
                  }}
                />

                <Box
                  sx={{ display: "flex", gap: 1, alignItems: "center", mt: 2 }}
                >
                  <TextField
                    fullWidth
                    label="短信验证码"
                    {...registerSms("code")}
                    error={!!smsErrors.code}
                    helperText={smsErrors.code?.message}
                    disabled={loading}
                    placeholder="请输入6位验证码"
                    sx={{
                      "& .MuiInputBase-root": {
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        "&:hover": {
                          backgroundColor: "rgba(255, 255, 255, 0.95)",
                        },
                        "&.Mui-focused": {
                          backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                      },
                    }}
                  />
                  <Button
                    variant="outlined"
                    onClick={sendSmsCode}
                    disabled={smsLoading || countdown > 0 || loading}
                    sx={{ whiteSpace: "nowrap", height: "40px" }}
                  >
                    {countdown > 0 ? `${countdown}s` : "发送验证码"}
                  </Button>
                </Box>

                <AgreementCheckbox
                  checked={agreementChecked}
                  onChange={setAgreementChecked}
                  disabled={loading}
                />

                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{ mt: 2, mb: 2, py: 1.5 }}
                  disabled={loading || !agreementChecked}
                >
                  {loading ? (
                    <CircularProgress size={24} color="inherit" />
                  ) : (
                    "登录"
                  )}
                </Button>
              </form>
            )}

            {/* 微信登录按钮 */}
            <Button
              variant="outlined"
              fullWidth
              onClick={() => setWechatDialogOpen(true)}
              sx={{
                mb: 2,
                py: 1.5,
                borderColor: "#07C160",
                color: "#07C160",
                "&:hover": {
                  borderColor: "#07C160",
                  backgroundColor: "rgba(7, 193, 96, 0.04)",
                },
              }}
            >
              <img
                src="/imgs/wechat-icon1.svg"
                alt="微信"
                style={{ width: 20, height: 20, marginRight: 8 }}
              />
              微信扫码登录
            </Button>

            {/* 注册链接 */}
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Typography variant="body2" color="text.secondary">
                还没有账号？
                <Typography
                  component="span"
                  onClick={() => setRegisterDialogOpen(true)}
                  sx={{
                    color: "primary.main",
                    ml: 1,
                    fontWeight: 600,
                    textDecoration: "underline",
                    cursor: "pointer",
                    "&:hover": {
                      color: "primary.dark",
                    },
                  }}
                >
                  立即注册
                </Typography>
              </Typography>
            </Box>
          </Paper>
        </Box>
      </Container>

      {/* 微信登录对话框 */}
      <WechatLoginDialog
        open={wechatDialogOpen}
        onClose={() => setWechatDialogOpen(false)}
        onSuccess={handleWechatLoginSuccess}
      />

      {/* 注册确认对话框 */}
      <Dialog
        open={registerDialogOpen}
        onClose={() => setRegisterDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ textAlign: "center", pb: 1 }}>
          <Typography
            variant="h6"
            component="div"
            sx={{ fontWeight: "bold", color: "primary.main" }}
          >
            注册前重要提醒
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ px: 3, py: 2 }}>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            您好！本平台实行企业管理员制度，每个公司仅能注册一个主账号。
          </Typography>
          <Typography variant="body1" sx={{ mb: 2, lineHeight: 1.6 }}>
            该账号将自动成为您公司的超级管理员，拥有包括：
          </Typography>

          <Box sx={{ ml: 2, mb: 2 }}>
            <Typography
              variant="body1"
              sx={{ mb: 1, display: "flex", alignItems: "center" }}
            >
              <span style={{ color: "#4caf50", marginRight: "8px" }}>✅</span>
              为员工创建和管理子账号
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 1, display: "flex", alignItems: "center" }}
            >
              <span style={{ color: "#4caf50", marginRight: "8px" }}>✅</span>
              代表公司进行购买和支付
            </Typography>
            <Typography
              variant="body1"
              sx={{ mb: 1, display: "flex", alignItems: "center" }}
            >
              <span style={{ color: "#4caf50", marginRight: "8px" }}>✅</span>
              查看公司所有项目和订单
            </Typography>
          </Box>

          <Typography
            variant="body1"
            sx={{ mb: 2, lineHeight: 1.6, fontWeight: "bold" }}
          >
            请确保您是由公司授权、负责该项工作的管理人员。
          </Typography>
          <Typography
            variant="body1"
            sx={{ lineHeight: 1.6, color: "text.secondary" }}
          >
            如非管理人员，请勿注册，请联系您公司的IT或采购部门。
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
          <Button
            onClick={() => setRegisterDialogOpen(false)}
            variant="outlined"
            sx={{ minWidth: 100 }}
          >
            取消
          </Button>
          <Button
            onClick={() => {
              setRegisterDialogOpen(false);
              navigate("/register");
            }}
            variant="contained"
            sx={{ minWidth: 100 }}
          >
            我已知悉，并继续注册
          </Button>
        </DialogActions>
      </Dialog>

      {/* 备案信息 - 固定在页面底部 */}
      <Box
        sx={{
          textAlign: "center",
          py: 2,
          backgroundColor: "rgba(255, 255, 255, 0.1)",
          backdropFilter: "blur(10px)",
          position: "fixed",
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 3,
        }}
      >
        <Typography variant="body2" sx={{ color: "rgba(255, 255, 255, 0.8)" }}>
          昭元绩效AI平台
          <MuiLink
            href="https://beian.miit.gov.cn/"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              color: "rgba(255, 255, 255, 0.9)",
              textDecoration: "none",
              ml: 1,
              "&:hover": {
                textDecoration: "underline",
                color: "#ffffff",
              },
            }}
          >
            豫ICP备2025140414号-1
          </MuiLink>
        </Typography>
      </Box>
    </Box>
  );
};

export default LoginPage;
